/**
 * Vista: Órdenes de Producción (AMX-PR-FR-01).
 *
 * Lo que esta pantalla tiene que dejar ver de un vistazo, y por eso está armada
 * así: qué se está trabajando AHORA, qué está DETENIDO y por qué. El resto —los
 * históricos, las canceladas— es archivo y va abajo.
 *
 * Por eso el orden de la tabla no es por fecha: primero en proceso, luego
 * pausadas, luego abiertas. Una orden pausada por falta de refacción es la que
 * necesita que alguien haga algo hoy.
 */

import ordenProduccionService from '../services/ordenProduccion.service.js';
import router from '../router/router.js';
import permisos from '../services/permissions.service.js';

const ETIQUETA_ESTADO = {
  abierta: 'Abierta',
  en_proceso: 'En proceso',
  pausada: 'Detenida',
  terminada: 'Terminada',
  cancelada: 'Cancelada',
};

const COLOR_ESTADO = {
  abierta: 'var(--info, #2c5c7a)',
  en_proceso: 'var(--primary, #1f5a7a)',
  pausada: 'var(--warning, #96601a)',
  terminada: 'var(--success, #2e6b4f)',
  cancelada: 'var(--text-muted, #75868c)',
};

function badge(estado) {
  return `<span style="display:inline-block;padding:2px 9px;border-radius:10px;font-size:.75rem;
    font-weight:600;color:#fff;background:${COLOR_ESTADO[estado] || '#777'};white-space:nowrap;">
    ${ETIQUETA_ESTADO[estado] || estado}</span>`;
}

function esc(s) {
  return String(s === null || s === undefined ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Horas a algo legible: 5 → "5 h"; 30 → "1 d 6 h". */
function horas(h) {
  const n = Number(h);
  if (!isFinite(n) || n < 0) return '—';
  if (n < 24) return `${n} h`;
  return `${Math.floor(n / 24)} d ${n % 24} h`;
}

export default {
  async render() {
    const tipos = ordenProduccionService.TIPOS.map(t => `<option value="${t.valor}">${t.texto}</option>`).join('');
    return `
      <div class="card">
        <div class="card-header" style="display:flex;justify-content:space-between;align-items:center;gap:1rem;flex-wrap:wrap;">
          <div>
            <h2 class="card-title">Órdenes de Producción</h2>
            <div style="color:var(--text-muted);font-size:.85rem;">
              Ensamble en planta · formato AMX-PR-FR-01
            </div>
          </div>
          ${permisos.canCreate('ordenes_produccion') ? `
            <button class="btn btn-primary" onclick="window.ordenesProduccionView.abrirNueva()">
              + Nueva Orden
            </button>` : ''}
        </div>

        <div class="stats-container" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:1rem;margin-bottom:1.5rem;">
          <div class="stat-card" style="background:var(--bg-secondary);padding:1rem;border-radius:8px;text-align:center;">
            <div id="stEnProceso" style="font-size:1.5rem;font-weight:bold;color:${COLOR_ESTADO.en_proceso};">-</div>
            <div style="color:var(--text-muted);font-size:.875rem;">En proceso</div>
          </div>
          <div class="stat-card" style="background:var(--bg-secondary);padding:1rem;border-radius:8px;text-align:center;">
            <div id="stPausadas" style="font-size:1.5rem;font-weight:bold;color:${COLOR_ESTADO.pausada};">-</div>
            <div style="color:var(--text-muted);font-size:.875rem;">Detenidas</div>
          </div>
          <div class="stat-card" style="background:var(--bg-secondary);padding:1rem;border-radius:8px;text-align:center;">
            <div id="stAbiertas" style="font-size:1.5rem;font-weight:bold;">-</div>
            <div style="color:var(--text-muted);font-size:.875rem;">Abiertas</div>
          </div>
          <div class="stat-card" style="background:var(--bg-secondary);padding:1rem;border-radius:8px;text-align:center;">
            <div id="stTerminadas" style="font-size:1.5rem;font-weight:bold;color:${COLOR_ESTADO.terminada};">-</div>
            <div style="color:var(--text-muted);font-size:.875rem;">Terminadas</div>
          </div>
          <div class="stat-card" style="background:var(--bg-secondary);padding:1rem;border-radius:8px;text-align:center;">
            <div id="stHorasDet" style="font-size:1.5rem;font-weight:bold;color:${COLOR_ESTADO.pausada};">-</div>
            <div style="color:var(--text-muted);font-size:.875rem;">Horas detenidas (prom.)</div>
          </div>
        </div>

        <!-- Por qué se detiene la línea. Es la pregunta que la orden vino a contestar. -->
        <div id="motivosBox" style="display:none;background:var(--bg-secondary);border-radius:8px;padding:.9rem 1.1rem;margin-bottom:1.5rem;">
          <div style="font-size:.78rem;text-transform:uppercase;letter-spacing:.06em;color:var(--text-muted);margin-bottom:.5rem;">
            Horas perdidas por motivo
          </div>
          <div id="motivosLista" style="display:flex;flex-wrap:wrap;gap:.6rem;"></div>
        </div>

        <div class="filters" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:1rem;margin-bottom:1.5rem;">
          <div class="form-group">
            <label class="form-label">Buscar</label>
            <input type="text" id="opBusqueda" class="form-input" placeholder="Folio, num. serie, descripción...">
          </div>
          <div class="form-group">
            <label class="form-label">Estado</label>
            <select id="opEstado" class="form-input">
              <option value="">Todos</option>
              <option value="en_proceso">En proceso</option>
              <option value="pausada">Detenidas</option>
              <option value="abierta">Abiertas</option>
              <option value="terminada">Terminadas</option>
              <option value="cancelada">Canceladas</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Tipo</label>
            <select id="opTipo" class="form-input"><option value="">Todos</option>${tipos}</select>
          </div>
          <div class="form-group" style="display:flex;align-items:flex-end;">
            <button class="btn btn-secondary" onclick="window.ordenesProduccionView.limpiarFiltros()" style="width:100%;">
              Limpiar filtros
            </button>
          </div>
        </div>

        <div class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Num. Serie</th>
                <th class="tbl-hide-mobile">Modelo</th>
                <th>Estado</th>
                <th class="tbl-hide-mobile">Tipo</th>
                <th class="tbl-hide-mobile">Técnico</th>
                <th class="tbl-hide-mobile">Trabajadas</th>
                <th class="tbl-hide-mobile">Detenida</th>
                <th class="tbl-hide-mobile">Retraso</th>
              </tr>
            </thead>
            <tbody id="opTableBody">
              <tr><td colspan="9" style="text-align:center;padding:2rem;">Cargando órdenes...</td></tr>
            </tbody>
          </table>
        </div>

        <div id="opPagination" class="pagination" style="margin-top:1rem;"></div>
      </div>

      <!-- Alta -->
      <div id="opModalNueva" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:1000;
           align-items:center;justify-content:center;padding:1rem;">
        <div class="card" style="max-width:560px;width:100%;max-height:90vh;overflow:auto;margin:0;">
          <div class="card-header"><h3 class="card-title">Nueva orden de producción</h3></div>
          <form id="opFormNueva">
            <div class="form-group">
              <label class="form-label">Número de serie <span style="color:var(--danger,#a33)">*</span></label>
              <input type="text" name="num_serie" class="form-input" required placeholder="Ej: FTTM5A34HSM000287">
              <div id="opAvisoSerie" style="font-size:.8rem;margin-top:.35rem;"></div>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;">
              <div class="form-group">
                <label class="form-label">Tipo de trabajo</label>
                <select name="tipo" class="form-input">${tipos}</select>
              </div>
              <div class="form-group">
                <label class="form-label">Prioridad</label>
                <select name="prioridad" class="form-input">
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                  <option value="baja">Baja</option>
                </select>
              </div>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;">
              <div class="form-group">
                <label class="form-label">Línea</label>
                <input type="text" name="linea" class="form-input" placeholder="Ej: L1">
              </div>
              <div class="form-group">
                <label class="form-label">Técnico asignado</label>
                <input type="text" name="tecnico" class="form-input">
              </div>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;">
              <div class="form-group">
                <label class="form-label">Inicio planeado</label>
                <input type="date" name="fecha_inicio_plan" class="form-input">
              </div>
              <div class="form-group">
                <label class="form-label">Fin planeado</label>
                <input type="date" name="fecha_fin_plan" class="form-input">
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Descripción del trabajo</label>
              <textarea name="descripcion" class="form-input" rows="3"></textarea>
            </div>
            <div style="display:flex;gap:.75rem;justify-content:flex-end;margin-top:1rem;">
              <button type="button" class="btn btn-secondary" onclick="window.ordenesProduccionView.cerrarNueva()">Cancelar</button>
              <button type="submit" class="btn btn-primary">Crear orden</button>
            </div>
          </form>
        </div>
      </div>
    `;
  },

  async afterRender() {
    if (!permisos.canView('ordenes_produccion')) {
      if (permisos.primeraRuta) { router.navigate(permisos.primeraRuta); }
      else {
        document.getElementById('content').innerHTML =
          '<div style="display:flex;justify-content:center;align-items:center;height:60vh;"><div style="text-align:center;color:var(--primary);"><h2>Acceso denegado</h2><p>No tienes permisos para acceder a este módulo.</p></div></div>';
      }
      return;
    }
    window.ordenesProduccionView = this;

    this.currentPage = 1;
    this.limit = 15;
    this.filters = {};

    const busq = document.getElementById('opBusqueda');
    busq.addEventListener('input', (e) => {
      clearTimeout(this._t);
      this._t = setTimeout(() => {
        this.filters.search = e.target.value;
        this.currentPage = 1;
        this.cargar();
      }, 450);
    });
    document.getElementById('opEstado').addEventListener('change', (e) => {
      this.filters.estado = e.target.value; this.currentPage = 1; this.cargar();
    });
    document.getElementById('opTipo').addEventListener('change', (e) => {
      this.filters.tipo = e.target.value; this.currentPage = 1; this.cargar();
    });
    document.getElementById('opFormNueva').addEventListener('submit', (e) => this.crear(e));

    // Aviso temprano: si el tractor ya trae una orden viva, se dice ANTES de que
    // el usuario llene todo el formulario y el servidor lo rechace al final.
    const inputSerie = document.querySelector('#opFormNueva [name="num_serie"]');
    inputSerie.addEventListener('blur', () => this.revisarSerie(inputSerie.value));

    await this.cargarEstadisticas();
    await this.cargar();
  },

  async cargarEstadisticas() {
    try {
      const r = await ordenProduccionService.estadisticas();
      const d = r.data || r;
      const c = d.conteos || {};
      document.getElementById('stEnProceso').textContent = c.en_proceso || 0;
      document.getElementById('stPausadas').textContent = c.pausadas || 0;
      document.getElementById('stAbiertas').textContent = c.abiertas || 0;
      document.getElementById('stTerminadas').textContent = c.terminadas || 0;
      document.getElementById('stHorasDet').textContent =
        d.tiempos && d.tiempos.horas_detenida_prom != null ? horas(Math.round(d.tiempos.horas_detenida_prom)) : '—';

      const motivos = d.pausas_por_motivo || [];
      const box = document.getElementById('motivosBox');
      if (motivos.length) {
        const texto = Object.fromEntries(ordenProduccionService.MOTIVOS_PAUSA.map(m => [m.valor, m.texto]));
        document.getElementById('motivosLista').innerHTML = motivos.map(m => `
          <span style="background:var(--bg-primary,#fff);border:1px solid var(--border,#ccc);border-radius:6px;
                padding:.35rem .7rem;font-size:.85rem;">
            <strong>${esc(texto[m.motivo] || m.motivo)}</strong>
            · ${horas(Math.round(Number(m.horas) || 0))}
            <span style="color:var(--text-muted);">(${m.veces})</span>
          </span>`).join('');
        box.style.display = 'block';
      } else {
        box.style.display = 'none';
      }
    } catch (e) {
      console.error('Error al cargar estadísticas de órdenes de producción:', e);
    }
  },

  async cargar() {
    try {
      const r = await ordenProduccionService.listar({
        page: this.currentPage, limit: this.limit, ...this.filters,
      });
      const ordenes = r.data || [];
      this.pintar(Array.isArray(ordenes) ? ordenes : []);
      this.pintarPaginacion(r.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (e) {
      console.error('Error al cargar órdenes de producción:', e);
      if (window.toast) toast.error('No se pudieron cargar las órdenes');
      this.pintar([]);
    }
  },

  pintar(ordenes) {
    const tbody = document.getElementById('opTableBody');
    if (!ordenes.length) {
      tbody.innerHTML = `<tr><td colspan="9" style="text-align:center;padding:2rem;color:var(--text-muted);">
        No hay órdenes que coincidan con el filtro.</td></tr>`;
      return;
    }
    tbody.innerHTML = ordenes.map(o => `
      <tr style="cursor:pointer;" onclick="router.navigate('/produccion/ordenes-produccion/${o.id}')">
        <td><strong>${esc(o.folio)}</strong>
          ${o.prioridad === 'alta' ? '<span title="Prioridad alta" style="color:var(--danger,#a33);">▲</span>' : ''}
        </td>
        <td style="font-family:monospace;font-size:.85rem;">${esc(o.num_serie)}</td>
        <td class="tbl-hide-mobile">${esc(o.modelo || '')}</td>
        <td>${badge(o.estado)}</td>
        <td class="tbl-hide-mobile">${esc(o.tipo)}</td>
        <td class="tbl-hide-mobile">${esc(o.tecnico || '')}</td>
        <td class="tbl-hide-mobile">${o.fecha_inicio_real ? horas(o.horas_efectivas) : '—'}</td>
        <td class="tbl-hide-mobile" style="${Number(o.horas_detenida) > 0 ? 'color:' + COLOR_ESTADO.pausada + ';font-weight:600;' : ''}">
          ${Number(o.horas_detenida) > 0 ? horas(o.horas_detenida) : '—'}
        </td>
        <td class="tbl-hide-mobile" style="${o.dias_de_retraso ? 'color:var(--danger,#a33);font-weight:600;' : ''}">
          ${o.dias_de_retraso ? o.dias_de_retraso + ' d' : '—'}
        </td>
      </tr>`).join('');
  },

  pintarPaginacion(p) {
    const cont = document.getElementById('opPagination');
    const total = p.totalPages || 1;
    if (total <= 1) { cont.innerHTML = ''; return; }
    let html = '<div style="display:flex;gap:.5rem;align-items:center;justify-content:center;">';
    html += `<button class="btn btn-secondary" ${p.page <= 1 ? 'disabled' : ''}
             onclick="window.ordenesProduccionView.irA(${p.page - 1})">Anterior</button>`;
    html += `<span style="color:var(--text-muted);">Página ${p.page} de ${total} · ${p.total} órdenes</span>`;
    html += `<button class="btn btn-secondary" ${p.page >= total ? 'disabled' : ''}
             onclick="window.ordenesProduccionView.irA(${p.page + 1})">Siguiente</button>`;
    cont.innerHTML = html + '</div>';
  },

  irA(p) { this.currentPage = p; this.cargar(); },

  limpiarFiltros() {
    this.filters = {};
    this.currentPage = 1;
    document.getElementById('opBusqueda').value = '';
    document.getElementById('opEstado').value = '';
    document.getElementById('opTipo').value = '';
    this.cargar();
  },

  abrirNueva() {
    document.getElementById('opAvisoSerie').innerHTML = '';
    document.getElementById('opModalNueva').style.display = 'flex';
  },

  cerrarNueva() {
    document.getElementById('opModalNueva').style.display = 'none';
    document.getElementById('opFormNueva').reset();
  },

  /** Avisa si el tractor ya tiene una orden sin cerrar. No bloquea: informa. */
  async revisarSerie(serie) {
    const aviso = document.getElementById('opAvisoSerie');
    if (!serie || !serie.trim()) { aviso.innerHTML = ''; return; }
    try {
      const r = await ordenProduccionService.abiertaPorSerie(serie.trim());
      const o = r.data || r;
      if (o && o.folio) {
        aviso.innerHTML = `<span style="color:var(--warning,#96601a);">
          Este tractor ya tiene la orden <strong>${esc(o.folio)}</strong> sin cerrar (${esc(ETIQUETA_ESTADO[o.estado] || o.estado)}).
          Ciérrala antes de abrir otra.</span>`;
      }
    } catch (e) {
      // 404 = no tiene orden viva, que es lo normal al dar de alta.
      aviso.innerHTML = '';
    }
  },

  async crear(ev) {
    ev.preventDefault();
    const fd = new FormData(ev.target);
    const data = {};
    fd.forEach((v, k) => { if (String(v).trim() !== '') data[k] = v; });

    const boton = ev.target.querySelector('button[type="submit"]');
    boton.disabled = true;
    const textoOriginal = boton.textContent;
    boton.textContent = 'Creando...';
    try {
      const r = await ordenProduccionService.crear(data);
      const o = (r.data || r);
      if (window.toast) toast.success(`Orden ${o.folio} creada`);
      this.cerrarNueva();
      await this.cargarEstadisticas();
      await this.cargar();
      router.navigate(`/produccion/ordenes-produccion/${o.id}`);
    } catch (e) {
      if (window.toast) toast.error(e.message || 'No se pudo crear la orden');
      console.error(e);
    } finally {
      boton.disabled = false;
      boton.textContent = textoOriginal;
    }
  },
};
